/**
 * Hooks de **mutación** del dominio Inventario (TanStack Query).
 *
 * Cubre el alta de rollos, el consumo, el borrado y los toggles admin por sede.
 * Tras éxito **invalidan las queries afectadas** (`docs/frontend_conventions.md`,
 * Datos): las mutaciones de rollos invalidan los rollos de la sede; los toggles,
 * el overview admin. El gating de permiso lo decide la UI antes de exponer la
 * acción; aquí solo se ejecuta y se propaga el `ApiError` (la pantalla muestra
 * `{error_code, message}`).
 *
 * Multi-sede de los toggles: el overview admin es global, pero el toggle apunta a
 * la sede de la fila, no a la sede activa. Como `lib/api` inyecta `X-Sede-Id`
 * desde el store de sede activa (un interceptor no puede leer props), el toggle
 * fija temporalmente la sede activa a la sede objetivo y la restaura al terminar:
 * así la petición lleva el `X-Sede-Id` correcto sin dejar a otras pantallas
 * apuntando a otra sede.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { getActiveSedeId, setActiveSedeId } from "@/lib/sede";

import {
  addRollo,
  consumeRollo,
  deleteRollo,
  toggleInventario,
  toggleLaser,
} from "../api";
import type { AddRolloBody, ConsumeBody, ToggleResult } from "../types";
import { ADMIN_OVERVIEW_KEY, rollosQueryKey } from "./queryKeys";

/** Invalida los rollos de la sede activa (lista + historial). */
function useInvalidateRollos() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      queryKey: rollosQueryKey(getActiveSedeId()),
    });
  };
}

/** Alta de rollos (galv/acero) en la sede activa (`POST /inventario/rollos`). */
export function useAddRollo() {
  const invalidate = useInvalidateRollos();
  return useMutation<{ ok: boolean }, Error, AddRolloBody>({
    mutationFn: (body) => addRollo(body),
    onSuccess: invalidate,
  });
}

/** Consume cantidad de un rollo de la sede activa (`POST /inventario/consume`). */
export function useConsumeRollo() {
  const invalidate = useInvalidateRollos();
  return useMutation<{ ok: boolean }, Error, ConsumeBody>({
    mutationFn: (body) => consumeRollo(body),
    onSuccess: invalidate,
  });
}

/** Elimina un rollo por id de la sede activa (`DELETE /inventario/rollos/<id>`). */
export function useDeleteRollo() {
  const invalidate = useInvalidateRollos();
  return useMutation<void, Error, number>({
    mutationFn: (rolloId) => deleteRollo(rolloId),
    onSuccess: invalidate,
  });
}

/**
 * Ejecuta un toggle apuntando a la sede `sedeId` por `X-Sede-Id`: fija la sede
 * activa a la objetivo durante la petición y la restaura al terminar (éxito o
 * error), para que el interceptor de `lib/api` envíe el header correcto sin
 * efectos colaterales en otras pantallas.
 */
async function withTargetSede(
  sedeId: number,
  action: (sedeId: number) => Promise<ToggleResult>,
): Promise<ToggleResult> {
  const previous = getActiveSedeId();
  setActiveSedeId(sedeId);
  try {
    return await action(sedeId);
  } finally {
    setActiveSedeId(previous);
  }
}

/** Activa/desactiva el módulo de inventario de una sede (admin, global overview). */
export function useToggleInventario() {
  const queryClient = useQueryClient();
  return useMutation<ToggleResult, Error, number>({
    mutationFn: (sedeId) => withTargetSede(sedeId, toggleInventario),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_OVERVIEW_KEY });
    },
  });
}

/** Activa/desactiva el horario láser de una sede (admin, global overview). */
export function useToggleLaser() {
  const queryClient = useQueryClient();
  return useMutation<ToggleResult, Error, number>({
    mutationFn: (sedeId) => withTargetSede(sedeId, toggleLaser),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_OVERVIEW_KEY });
    },
  });
}
